/**
 * Duration and clock-time arithmetic for the time calculator.
 *
 * Everything is whole seconds. A duration typed as decimal hours (1.2345 h) is rounded to
 * the nearest second when it is read, and the widget says so; carrying fractions of a
 * second through h:mm:ss arithmetic would only produce displays like 1:14:04.2000001.
 *
 * Dates are counted in UTC calendar days (`Date.UTC`), so every day is exactly 24 hours.
 * That is the point: a span "from 22:30 on 7 March to 06:15 on 9 March" is 31:45 whatever
 * the reader's time zone, and no daylight-saving rule can shift it by an hour mid-sum.
 */

import { parseNumberInput } from "@/lib/parse-number";

export const SECONDS_PER_MINUTE = 60;
export const SECONDS_PER_HOUR = 3600;
export const SECONDS_PER_DAY = 86_400;
const MS_PER_DAY = 86_400_000;

/** U+2212, and the other dashes people paste, read as a minus sign. */
const LEADING_SIGN = /^[-−–]\s*/;
const PLUS_SIGN = /^\+\s*/;
const WHITESPACE = /\s+/g;

/** `h:mm` or `h:mm:ss`, hours unbounded, seconds may carry a decimal part. */
const COLON_DURATION = /^(\d+):([0-5]?\d)(?::([0-5]?\d(?:[.,]\d+)?))?$/;

/** A number and an optional unit word: `2h`, `30 min`, `1,5 h`, `45`. */
const UNIT_TOKEN = /(\d+(?:[.,]\d+)?)\s*([a-z]*)/gi;
const ONLY_SEPARATORS = /^[\s,]*(?:and[\s,]*)*$/i;

/** A 24-hour clock time from `<input type="time">`: `HH:MM` or `HH:MM:SS`. */
const CLOCK_TIME = /^([01]?\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Separators between the entries of a list: new lines, semicolons, or ", ". */
const LIST_SEPARATOR = /\n|;|,(?=\s)/;

type Unit = "d" | "h" | "m" | "s";

const UNIT_SECONDS: Record<Unit, number> = {
  d: SECONDS_PER_DAY,
  h: SECONDS_PER_HOUR,
  m: SECONDS_PER_MINUTE,
  s: 1,
};

/** The unit a bare number after this one means: `1h30` is 1 h 30 min. */
const NEXT_UNIT: Record<Unit, Unit | null> = {
  d: "h",
  h: "m",
  m: "s",
  s: null,
};

const UNIT_WORDS: Record<string, Unit> = {
  d: "d",
  day: "d",
  days: "d",
  h: "h",
  hr: "h",
  hrs: "h",
  hour: "h",
  hours: "h",
  m: "m",
  min: "m",
  mins: "m",
  minute: "m",
  minutes: "m",
  s: "s",
  sec: "s",
  secs: "s",
  second: "s",
  seconds: "s",
};

function readSign(text: string): { sign: 1 | -1; rest: string } {
  const trimmed = text.trim();
  if (LEADING_SIGN.test(trimmed)) {
    return { sign: -1, rest: trimmed.replace(LEADING_SIGN, "") };
  }
  return { sign: 1, rest: trimmed.replace(PLUS_SIGN, "") };
}

function parseColonDuration(text: string): number | null {
  const match = COLON_DURATION.exec(text);
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds =
    match[3] === undefined ? 0 : (parseNumberInput(match[3]) ?? Number.NaN);
  if (!Number.isFinite(seconds)) {
    return null;
  }
  return hours * SECONDS_PER_HOUR + minutes * SECONDS_PER_MINUTE + seconds;
}

function parseUnitDuration(text: string): number | null {
  let total = 0;
  let previous: Unit | null = null;
  let tokens = 0;
  let leftover = text;
  for (const match of text.matchAll(UNIT_TOKEN)) {
    const value = parseNumberInput(match[1]);
    if (value === null) {
      return null;
    }
    const word = match[2].toLowerCase();
    let unit: Unit | null = null;
    if (word === "") {
      // A bare number: hours on its own, otherwise the unit after the previous one.
      unit = previous === null ? "h" : NEXT_UNIT[previous];
      if (unit === null || (previous === null && tokens > 0)) {
        return null;
      }
    } else {
      unit = UNIT_WORDS[word] ?? null;
      if (unit === null) {
        return null;
      }
    }
    total += value * UNIT_SECONDS[unit];
    previous = unit;
    tokens += 1;
    leftover = leftover.replace(match[0], " ");
  }
  if (tokens === 0 || !ONLY_SEPARATORS.test(leftover)) {
    return null;
  }
  return total;
}

/**
 * A typed duration in whole seconds, or null.
 *
 * Accepts `h:mm`, `h:mm:ss`, unit forms (`1h 45m 30s`, `90 min`, `1.5 h`, `1h30`), and a
 * bare number, which counts as hours. A leading minus makes it negative.
 */
export function parseDuration(text: string): number | null {
  const { sign, rest } = readSign(text);
  if (rest === "") {
    return null;
  }
  const compact = rest.replace(WHITESPACE, " ");
  const seconds = compact.includes(":")
    ? parseColonDuration(compact.replace(WHITESPACE, ""))
    : parseUnitDuration(compact);
  if (seconds === null || !Number.isFinite(seconds)) {
    return null;
  }
  return sign * Math.round(seconds);
}

export interface HmsParts {
  negative: boolean;
  hours: number;
  minutes: number;
  seconds: number;
}

export function splitHms(totalSeconds: number): HmsParts {
  const abs = Math.abs(Math.round(totalSeconds));
  return {
    negative: totalSeconds < 0 && abs > 0,
    hours: Math.floor(abs / SECONDS_PER_HOUR),
    minutes: Math.floor((abs % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE),
    seconds: abs % SECONDS_PER_MINUTE,
  };
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

/** `4:16:15`, `−0:45:00`, `26:00:00` — hours are not wrapped at 24. */
export function formatHms(totalSeconds: number): string {
  const parts = splitHms(totalSeconds);
  const sign = parts.negative ? "−" : "";
  return `${sign}${parts.hours}:${pad2(parts.minutes)}:${pad2(parts.seconds)}`;
}

/** A clock time as `HH:MM:SS`. */
export function formatClock(secondsOfDay: number): string {
  const parts = splitHms(secondsOfDay);
  return `${pad2(parts.hours)}:${pad2(parts.minutes)}:${pad2(parts.seconds)}`;
}

/** Seconds since midnight for `HH:MM` or `HH:MM:SS`, or null. */
export function parseClockTime(text: string): number | null {
  const match = CLOCK_TIME.exec(text.trim());
  if (!match) {
    return null;
  }
  return (
    Number(match[1]) * SECONDS_PER_HOUR +
    Number(match[2]) * SECONDS_PER_MINUTE +
    Number(match[3] ?? 0)
  );
}

/** Days since 1970-01-01 for `YYYY-MM-DD`, or null for anything that is not a real date. */
export function parseIsoDay(text: string): number | null {
  const match = ISO_DATE.exec(text.trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const ms = Date.UTC(year, month - 1, day);
  const check = new Date(ms);
  if (check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) {
    return null;
  }
  return Math.round(ms / MS_PER_DAY);
}

export type ElapsedKind = "same-day" | "overnight" | "dated";

export type ElapsedResult =
  | {
      ok: true;
      seconds: number;
      kind: ElapsedKind;
      /** Whole days between the two dates (dated spans only). */
      days: number;
    }
  | { ok: false; error: "one-date" | "end-before-start" };

/**
 * The time from a start clock time to an end clock time.
 *
 * Without dates the span is at most a day: an end earlier than the start is taken to be
 * the next morning (a night shift), and equal times are zero. With both dates the span is
 * exact and can be any length.
 */
export function elapsedBetween(
  start: number,
  end: number,
  startDay: number | null,
  endDay: number | null
): ElapsedResult {
  if ((startDay === null) !== (endDay === null)) {
    return { ok: false, error: "one-date" };
  }
  if (startDay !== null && endDay !== null) {
    const days = endDay - startDay;
    const seconds = days * SECONDS_PER_DAY + (end - start);
    if (seconds < 0) {
      return { ok: false, error: "end-before-start" };
    }
    return { ok: true, seconds, kind: "dated", days };
  }
  if (end < start) {
    return {
      ok: true,
      seconds: end + SECONDS_PER_DAY - start,
      kind: "overnight",
      days: 0,
    };
  }
  return { ok: true, seconds: end - start, kind: "same-day", days: 0 };
}

export interface ListEntry {
  text: string;
  seconds: number | null;
  /** The total of every readable entry up to and including this one. */
  runningTotal: number;
}

export interface ListSum {
  entries: ListEntry[];
  total: number;
  readable: number;
  unreadable: number;
}

/** Add up a pasted list of durations — one per line, or separated by semicolons. */
export function sumDurations(text: string): ListSum {
  const entries: ListEntry[] = [];
  let total = 0;
  let readable = 0;
  let unreadable = 0;
  for (const raw of text.split(LIST_SEPARATOR)) {
    const entry = raw.trim();
    if (entry === "") {
      continue;
    }
    const seconds = parseDuration(entry);
    if (seconds === null) {
      unreadable += 1;
    } else {
      total += seconds;
      readable += 1;
    }
    entries.push({ text: entry, seconds, runningTotal: total });
  }
  return { entries, total, readable, unreadable };
}

/** Seconds as decimal hours, unrounded. */
export function toDecimalHours(seconds: number): number {
  return seconds / SECONDS_PER_HOUR;
}

/** Decimal hours as whole seconds — the rounding the widget reports. */
export function fromDecimalHours(hours: number): {
  exactSeconds: number;
  seconds: number;
} {
  const exactSeconds = hours * SECONDS_PER_HOUR;
  return { exactSeconds, seconds: Math.round(exactSeconds) };
}
